import React, { useState } from 'react';
import { ExploreConfig } from '@explore-with-ai/core';
import './CodeGenerator.css';

interface CodeGeneratorProps {
  config: ExploreConfig;
  onBack: () => void;
}

const CodeGenerator: React.FC<CodeGeneratorProps> = ({ config, onBack }) => {
  const [outputType, setOutputType] = useState<'embed' | 'standalone' | 'json' | 'react'>('embed');
  const [copied, setCopied] = useState(false);

  const generateEmbedCode = () => {
    
    return `<!-- Explore with AI Widget -->
<div data-explore-with-ai='${JSON.stringify(config).replace(/'/g, "&apos;")}'></div>
<script>
(function() {
  // Auto-mount ExploreWithAI widgets
  function loadExploreWithAI() {
    const widgets = document.querySelectorAll('[data-explore-with-ai]');
    
    widgets.forEach(element => {
      const configData = element.getAttribute('data-explore-with-ai');
      if (!configData || element.dataset.mounted) return;
      
      try {
        const config = JSON.parse(configData);
        element.dataset.mounted = 'true';
        
        // Create widget HTML
        element.innerHTML = \`
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <button onclick="openExploreAI_\${config.id}()" style="
              display: inline-flex;
              align-items: center;
              gap: 0.5rem;
              padding: 0.75rem 1.5rem;
              background: #667eea;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            " onmouseover="this.style.background='#5a67d8'" onmouseout="this.style.background='#667eea'">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              Explore with AI
            </button>
          </div>
        \`;
        
        // Create launch function
        window['openExploreAI_' + config.id] = function() {
          const prompt = \`Research Topic: \${config.title}

Description: \${config.description}
Source: \${config.sourceName}
Interactive Page: \${config.sourceUrl}

Please visit the interactive page above and analyze the visualization. Provide:
- Key insights about the patterns shown
- Interpretation of what the data reveals
- Analysis over time
- Implications and recommendations\`;
          
          const encodedPrompt = encodeURIComponent(prompt);
          window.open(\`https://claude.ai/new?q=\${encodedPrompt}\`, '_blank');
          
          // Analytics
          console.log('ExploreWithAI launched:', config.title);
        };
      } catch (error) {
        console.error('Failed to mount ExploreWithAI widget:', error);
      }
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadExploreWithAI);
  } else {
    loadExploreWithAI();
  }
})();
</script>`;
  };

  const generateStandaloneHTML = () => {
    const configJson = JSON.stringify(config, null, 2);
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Explore with AI - ${config.title}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 2rem;
            background: #f9fafb;
        }
        
        .container {
            max-width: 600px;
            margin: 0 auto;
            background: white;
            padding: 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .title {
            font-size: 1.5rem;
            font-weight: 600;
            color: #111827;
            margin-bottom: 1rem;
        }
        
        .description {
            color: #6b7280;
            margin-bottom: 2rem;
            line-height: 1.6;
        }
        
        .explore-button {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1.5rem;
            background: #667eea;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .explore-button:hover {
            background: #5a67d8;
            transform: translateY(-1px);
        }
        
        .meta {
            margin-top: 2rem;
            padding-top: 2rem;
            border-top: 1px solid #e5e7eb;
            font-size: 0.875rem;
            color: #6b7280;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1 class="title">${config.title}</h1>
        <p class="description">${config.description}</p>
        
        <button class="explore-button" onclick="launchAnalysis()">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
            </svg>
            Explore with AI
        </button>
        
        <div class="meta">
            <p><strong>Source:</strong> ${config.sourceName}</p>
            <p><strong>Format:</strong> ${config.format}</p>
            ${config.lastUpdated ? `<p><strong>Last Updated:</strong> ${config.lastUpdated}</p>` : ''}
        </div>
    </div>

    <script>
        const config = ${configJson};
        
        function launchAnalysis() {
            const prompt = \`Research Topic: \${config.title}

Description: \${config.description}
Source: \${config.sourceName}
Interactive Page: \${config.sourceUrl}

Please visit the interactive page above and analyze the visualization. Provide:
- Key insights about the patterns shown
- Interpretation of what the data reveals
- Analysis over time
- Implications and recommendations\`;
            
            const encodedPrompt = encodeURIComponent(prompt);
            window.open(\`https://claude.ai/new?q=\${encodedPrompt}\`, '_blank');
            
            console.log('Analysis launched for:', config.title);
        }
    </script>
</body>
</html>`;
  };

  const generateJSONConfig = () => {
    return JSON.stringify(config, null, 2);
  };

  const generateReactExample = () => {
    return `import React from 'react';
import { ExploreWithAI } from '@explore-with-ai/widget';

const config = ${JSON.stringify(config, null, 2)};

const MyComponent = () => {
  const handleAnalytics = (event) => {
    console.log('Widget Analytics:', event);
    // Add your analytics tracking here
  };

  return (
    <div>
      <h1>${config.title}</h1>
      <p>${config.description}</p>
      
      <ExploreWithAI 
        config={config}
        onAnalytics={handleAnalytics}
      />
    </div>
  );
};

export default MyComponent;`;
  };

  const getGeneratedCode = () => {
    switch (outputType) {
      case 'embed':
        return generateEmbedCode();
      case 'standalone':
        return generateStandaloneHTML();
      case 'json':
        return generateJSONConfig();
      case 'react':
        return generateReactExample();
      default:
        return '';
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getGeneratedCode());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const downloadFile = () => {
    const code = getGeneratedCode();
    const filename = outputType === 'json' 
      ? `${config.id || 'widget'}-config.json`
      : outputType === 'react'
      ? `${config.id || 'widget'}-component.jsx`
      : outputType === 'standalone'
      ? `${config.id || 'widget'}.html`
      : `${config.id || 'widget'}-embed.html`;
    
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getOutputDescription = () => {
    switch (outputType) {
      case 'embed':
        return 'Inline HTML code that can be pasted directly into any webpage. Includes auto-mounting JavaScript.';
      case 'standalone':
        return 'Complete HTML file that can be hosted separately or opened directly in a browser.';
      case 'json':
        return 'JSON configuration file that can be used with the ExploreWithAI library programmatically.';
      case 'react':
        return 'React component example showing how to use the widget in a React application.';
      default:
        return '';
    }
  };

  return (
    <div className="code-generator">
      <div className="code-generator__card">
        <h2 className="code-generator__title">Export Your Widget</h2>
        <p className="code-generator__description">
          Choose how you want to export and use your Explore with AI widget.
        </p>

        <div className="code-generator__options">
          <div className="code-generator__option-grid">
            <label className="code-generator__option">
              <input
                type="radio"
                name="outputType"
                value="embed"
                checked={outputType === 'embed'}
                onChange={(e) => setOutputType(e.target.value as any)}
              />
              <div className="code-generator__option-content">
                <h3>Embed Code</h3>
                <p>Paste directly into HTML</p>
              </div>
            </label>

            <label className="code-generator__option">
              <input
                type="radio"
                name="outputType"
                value="standalone"
                checked={outputType === 'standalone'}
                onChange={(e) => setOutputType(e.target.value as any)}
              />
              <div className="code-generator__option-content">
                <h3>Standalone HTML</h3>
                <p>Complete HTML file</p>
              </div>
            </label>

            <label className="code-generator__option">
              <input
                type="radio"
                name="outputType"
                value="json"
                checked={outputType === 'json'}
                onChange={(e) => setOutputType(e.target.value as any)}
              />
              <div className="code-generator__option-content">
                <h3>JSON Config</h3>
                <p>Configuration only</p>
              </div>
            </label>

            <label className="code-generator__option">
              <input
                type="radio"
                name="outputType"
                value="react"
                checked={outputType === 'react'}
                onChange={(e) => setOutputType(e.target.value as any)}
              />
              <div className="code-generator__option-content">
                <h3>React Example</h3>
                <p>Component code</p>
              </div>
            </label>
          </div>

          <p className="code-generator__option-description">
            {getOutputDescription()}
          </p>
        </div>

        <div className="code-generator__output">
          <div className="code-generator__output-header">
            <h3>Generated Code</h3>
            <div className="code-generator__output-actions">
              <button
                onClick={copyToClipboard}
                className="code-generator__copy-button"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={downloadFile}
                className="code-generator__download-button"
              >
                Download
              </button>
            </div>
          </div>
          
          <pre className="code-generator__code">
            <code>{getGeneratedCode()}</code>
          </pre>
        </div>

        <div className="code-generator__actions">
          <button
            onClick={onBack}
            className="code-generator__back-button"
          >
            ← Back to Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeGenerator;
